import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ShareSkillModal from '../ShareSkillModal';
import AlertsCustom from '../Alerts';
import { CONTENT_PREVIEW_MAX_BYTES, downloadTextAsFile, formatMegabytes, textByteLength } from '../SkillsManagerModal/skillUpload';
import './SkillResultCard.css';

const FRONTMATTER_RE = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;

// Remove o frontmatter YAML (name/description) do SKILL.md antes de exibir -
// o preview mostra so o corpo, igual ao mockup do Figma, tanto em "Previa"
// quanto em "Codigo" (Tarefa 7.6).
function stripFrontmatter(content) {
  return content.replace(FRONTMATTER_RE, '').trimStart();
}

// Card de resultado exibido no chat apos o fluxo "/skill-creator" finalizar
// (Tarefa 1.2, specs/skills/plan.md) - nome, botao "Compartilhar" (abre o
// modal ShareSkillModal, Tarefa 4.1), preview do .md com scroll, e texto de
// exemplo de uso (FR-006).
//
// Tarefa 7.6 (FR-051): toggle "Previa"/"Codigo" - "Previa" (padrao) e' o
// markdown processado (react-markdown/remark-gfm, mesmo mecanismo ja usado
// em SkillDetailView/Message); "Codigo" e' o texto cru, comportamento unico
// que existia antes desta tarefa (fiel ao mockup do Figma original, FR-007).
// `[REVISADO em 2026-08-13]`: decisao tomada com o usuario de trocar o
// padrao de "Codigo" pra "Previa" ao adicionar o toggle - ver spec.md.
export default function SkillResultCard({ skill }) {
  const { t: tChat } = useTranslation('chat');
  const [currentSkill, setCurrentSkill] = useState(skill);
  const [showShareModal, setShowShareModal] = useState(false);
  const [viewMode, setViewMode] = useState('preview');
  // Achado (specs/skills/plan.md, 2026-09-15): sem isso, uma skill excluída DEPOIS que este
  // card já apareceu no chat fazia o "Compartilhar" falhar em silêncio - o ShareSkillModal
  // fecha de forma otimista antes da resposta do POST, e sem `onError` aqui o erro real
  // (ex.: skill não existe mais) nunca chegava a aparecer pro usuário.
  const [shareError, setShareError] = useState(null);
  const { name = '', content = '' } = currentSkill || {};
  const strippedContent = stripFrontmatter(content);
  // Bugfix pos-Tarefa 7.7: mesmo problema/mesma correcao de SkillDetailView.js
  // - acima deste tamanho, nem "Previa" (react-markdown/remark-gfm) nem
  // "Codigo" (<pre>) tentam renderizar o conteudo inteiro, o que travava a
  // aba. Nao deve acontecer na pratica (conteudo vem da IA, tipicamente KB),
  // mas o skill-creator/skill-editor nao limitam tamanho de conteudo hoje.
  const contentBytes = textByteLength(strippedContent);
  const contentTooLargeToPreview = contentBytes > CONTENT_PREVIEW_MAX_BYTES;

  return (
    <div className="skill-result-card" data-testid="skill-result-card">
      <div className="skill-result-header">
        <span className="skill-result-name" data-testid="skill-result-name">/{name}</span>
        <button
          type="button"
          className="skill-result-share-btn"
          data-testid="skill-result-share-button"
          onClick={() => setShowShareModal(true)}
        >
          {tChat('skill_share_button')}
        </button>
      </div>
      <div className="skill-result-preview" data-testid="skill-result-preview">
        {!contentTooLargeToPreview && (
          <div className="skill-result-view-toggle" data-testid="skill-result-view-toggle">
            <button
              type="button"
              className={`skill-result-view-toggle-button${viewMode === 'preview' ? ' skill-result-view-toggle-button--active' : ''}`}
              onClick={() => setViewMode('preview')}
              aria-label={tChat('skill_view_toggle_preview_label')}
              title={tChat('skill_view_toggle_preview_label')}
              data-testid="skill-result-view-toggle-preview"
            >
              <img src="/imagens/skills-eye.svg" alt="" />
            </button>
            <button
              type="button"
              className={`skill-result-view-toggle-button${viewMode === 'code' ? ' skill-result-view-toggle-button--active' : ''}`}
              onClick={() => setViewMode('code')}
              aria-label={tChat('skill_view_toggle_code_label')}
              title={tChat('skill_view_toggle_code_label')}
              data-testid="skill-result-view-toggle-code"
            >
              <img src="/imagens/skills-code.svg" alt="" />
            </button>
          </div>
        )}
        {contentTooLargeToPreview ? (
          <div className="skill-result-preview-too-large" data-testid="skill-result-preview-too-large">
            <p>{tChat('skills_detail_preview_too_large_message', { size: formatMegabytes(contentBytes) })}</p>
            <button
              type="button"
              className="skill-result-download-large-file-button"
              data-testid="skill-result-download-large-file"
              onClick={() => downloadTextAsFile(content, `${name || 'skill'}.md`)}
            >
              {tChat('skills_detail_preview_too_large_download_button')}
            </button>
          </div>
        ) : viewMode === 'preview' ? (
          <div className="skill-result-preview-markdown" data-testid="skill-result-preview-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{strippedContent}</ReactMarkdown>
          </div>
        ) : (
          <pre className="skill-result-preview-text" data-testid="skill-result-preview-code">{strippedContent}</pre>
        )}
      </div>
      <p className="skill-result-usage" data-testid="skill-result-usage">
        {tChat('skill_usage_example', { name })}
      </p>

      <ShareSkillModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        skill={currentSkill}
        onUpdated={setCurrentSkill}
        onError={setShareError}
      />

      {shareError && (
        <AlertsCustom
          type="error"
          title={tChat('alertErrorTitle')}
          message={shareError}
          onClose={() => setShareError(null)}
        />
      )}
    </div>
  );
}
